import { type FC, useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Paper,
  Container
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import PaymentInstructions from '../temporal/payment-instructions';

interface ExpandablePaymentInstructionsProps {
  defaultExpanded?: boolean;
  title?: string;
  subtitle?: string;
}

const ExpandablePaymentInstructions: FC<ExpandablePaymentInstructionsProps> = ({
  defaultExpanded = false,
  title = "Información de Pago de Cuota de Apoyo",
  subtitle = "Conoce las opciones disponibles para realizar tu pago"
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        pt: { xs: 2, sm: 2.5, md: 3, lg: 3.5 },
        pb: { xs: 2, sm: 2.5, md: 3, lg: 3.5 },
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          border: '1px solid #E0E0E0',
          borderRadius: { xs: 2, md: 2.5 },
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: { md: 'translateY(-4px)' },
            boxShadow: { md: '0 8px 24px rgba(0,0,0,0.12)' }
          }
        }}
      >
        {/* Header Section */}
        <Box
          onClick={handleExpandClick}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 1.5 },
            p: { xs: 2, sm: 2, md: 2.5, lg: 2.5 },
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #45538aff 0%, #303a63 100%)',
            color: 'white',
            transition: 'background 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #303a63 0%, #45538aff 100%)',
            }
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: { xs: 0.5, sm: 0.5 },
                fontSize: { xs: '1.125rem', sm: '1.188rem', md: '1.25rem', lg: '1.313rem' }
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.95,
                fontSize: { xs: '0.813rem', sm: '0.875rem', md: '0.875rem' },
                lineHeight: 1.5
              }}
            >
              {subtitle}
            </Typography>
          </Box>
          <IconButton
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="mostrar más"
            sx={{
              alignSelf: { xs: 'flex-end', sm: 'center' },
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              color: 'white',
              p: { xs: 0.75, sm: 1 },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)'
              }
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: { xs: 26, sm: 28, md: 30 } }} />
          </IconButton>
        </Box>

        {/* Collapsible Content */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              bgcolor: 'background.default',
              borderTop: '2px solid',
              borderColor: 'secondary.main'
            }}
          >
            <PaymentInstructions />
          </Box>
        </Collapse>
      </Paper>
    </Container>
  );
};

export default ExpandablePaymentInstructions;
